import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  EnumActivityLogType,
  EnumNoteKind,
  EnumOwnerType,
  NoteManyQuery,
  PersonDetailsQuery,
  useActivityLogManyQuery,
  useChangeLogManyPersonDetailsQuery,
  useMembershipTimelineManyQuery,
} from "gql/generated";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotesContext } from "shared/contextProviders";
import { useLoggedInAs } from "shared/hooks";
import { DATE_PATTERN_SIMPLE, getMultilingualString } from "shared/utils";

export enum TimelineFilter {
  INQUIRIES = "INQUIRIES",
  PURCHASES = "PURCHASES",
  ACTIVITY = "ACTIVITY",
  NOTES = "NOTES",
  BROADCASTS = "BROADCASTS",
  MEMBERSHIP = "MEMBERSHIP",
}

export enum TimelineItemTag {
  INQUIRY = "INQUIRY",
  FEE = "FEE",
  RENEWABLE_FEE = "RENEWABLE_FEE",
  ACCOMMODATION = "ACCOMMODATION",
  EVENT = "EVENT",
  MERCH = "MERCH",
  ACTIVITY = "ACTIVITY",
  NOTE = "NOTE",
  BROADCAST = "BROADCAST",
  MEMBERSHIP = "MEMBERSHIP",
}

const MEMBERSHIP_NOTE_KINDS = new Set<string>([
  EnumNoteKind.MembershipStatusAdded,
  EnumNoteKind.MembershipStatusRemoved,
  EnumNoteKind.MembershipTierAdded,
  EnumNoteKind.MembershipTierRemoved,
]);

export interface ITimelineItem {
  _id: string;
  groupByDate: string;
  filterGroup: TimelineFilter;
  tag: TimelineItemTag;
  title: string;
  description: string;
  status?: string;
  operator?: string | null;
  organization?: string | null;
  createdBy?: string;
  note?: NoteManyQuery["noteMany"][number];
  broadcastId?: string;
  surveyResponseId?: string;
  eventId?: string;
  leadType?: string;
  initiatedBy?: string;
  membershipKind?: string;
  membershipViaSuffix?: string;
  membershipActorName?: string | null;
}

export interface ITimelineGroup {
  groupByDate: string;
  items: ITimelineItem[];
}

export type UseTimelineProps = {
  person: PersonDetailsQuery["personDetails"];
};

export const useTimeline = ({ person }: UseTimelineProps) => {
  const loggedInAs = useLoggedInAs();

  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.TIMELINE",
  });

  const [timelineGroups, setTimelineGroups] = useState<ITimelineGroup[]>([]);
  const [filteredTimelineGroups, setFilteredTimelineGroups] = useState<
    ITimelineGroup[]
  >([]);

  /* ********************************************************
  /* CHANGELOG (ACTIVITY)
  /* - Load the changelog for the person
  /* - Merge the changelog with the timeline items
  /* - NOTE that we're using the lazy query here because if
  /*   the person does NOT have a userId, we still want to
  /*   trigger the merge code
  /* ***************************************************** */
  // Load the change log details for the person
  const {
    data: changeLogData,
    loading: changeLogLoading,
    error: changeLogError,
  } = useChangeLogManyPersonDetailsQuery({
    variables: {
      filter: {
        initiatedByUser: person?.userId,
        ...(loggedInAs &&
          loggedInAs.type !== EnumOwnerType.TnbInternal && {
            OR: [
              { owner: loggedInAs },
              ...(person?.operators?.map(operator => ({
                owner: {
                  entityId: operator?._entityId ?? "",
                  type: EnumOwnerType.Operator,
                },
              })) ?? []),
            ],
          }),
      },
      limit: 10000,
    },
    skip: !person || !person.userId || !loggedInAs,
  });

  /* ********************************************************
  /* ACTIVITY LOGS 
  /* - Load the broadcast related activity logs for the person
  /* ***************************************************** */
  const {
    data: activityLogsData,
    loading: activityLogsLoading,
    error: activityLogsError,
  } = useActivityLogManyQuery({
    variables: {
      filter: {
        initiatedByPerson: person?._id,
        types: [
          EnumActivityLogType.BroadcastPublished,
          EnumActivityLogType.BroadcastViewed,
          EnumActivityLogType.CourseCompleted,
          EnumActivityLogType.CourseStarted,
          EnumActivityLogType.FileDownloaded,
          EnumActivityLogType.FormCompleted,
          EnumActivityLogType.FormDraftSaved,
          EnumActivityLogType.FormResubmitted,
          EnumActivityLogType.FormSubmissionApproved,
          EnumActivityLogType.FormSubmissionRejected,
          EnumActivityLogType.PhotosDownloaded,
          EnumActivityLogType.VideoPlayed,
          EnumActivityLogType.EventSubmitted,
          EnumActivityLogType.InquiryReceived,
        ],
      },
    },
    skip: !person || !person._id,
    fetchPolicy: "cache-and-network",
  });

  /* ********************************************************
  /* MEMBERSHIP EVENTS
  /* ***************************************************** */
  const {
    data: membershipData,
    loading: membershipLoading,
    error: membershipError,
  } = useMembershipTimelineManyQuery({
    variables: {
      subjectType: "PERSON",
      subjectEntityId: person?._id ?? "",
      organizationId: loggedInAs?.entityId ?? "",
    },
    skip: !person?._id || !loggedInAs?.entityId,
    fetchPolicy: "cache-and-network",
  });

  /* ********************************************************
  /* NOTES
  /* ***************************************************** */
  const { notes, loading: notesLoading, error: notesError } = useNotesContext();

  /**
   * Merges the timeline data into the timeline groups
   * - This will run when the person changes, when the changelog data changes,
   *   when the activity logs data changes, and when the notes data changes
   */
  useEffect(() => {
    if (!person) return;

    const allItems: ITimelineItem[] = [
      // Orders will be returned with the Person details
      ...(person.orders
        ?.filter(order => !!order)
        .map(order => {
          let description = "";
          const isAccommodation = order.type === TimelineItemTag.ACCOMMODATION;
          if (isAccommodation) {
            const bookingStart = toZonedTime(order.dateStartISO ?? "", "UTC");
            const bookingEnd = toZonedTime(order.dateEndISO ?? "", "UTC");
            if (
              bookingStart.getMonth() === bookingEnd.getMonth() &&
              bookingStart.getFullYear() === bookingEnd.getFullYear()
            ) {
              description = `${format(bookingStart, "MMM d")} - ${format(bookingEnd, "d")}, ${bookingStart.getFullYear()}`;
            } else if (
              bookingStart.getFullYear() !== bookingEnd.getFullYear()
            ) {
              description = `${format(bookingStart, "MMM d, yyyy")} - ${format(bookingEnd, "MMM d, yyyy")}`;
            } else {
              description = `${format(bookingStart, "MMM d")} - ${format(bookingEnd, "MMM d")}`;
            }
          }
          return {
            _id: order._id,
            groupByDate: format(order.dateISO ?? "", DATE_PATTERN_SIMPLE),
            filterGroup: TimelineFilter.PURCHASES,
            tag: order.type as unknown as TimelineItemTag,
            title: getMultilingualString(order.name ?? {}) ?? "",
            description,
          };
        }) ?? []),
      // Leads will be returned with the Person details
      ...(person.leads
        ?.filter(lead => !!lead)
        .map(lead => ({
          _id: lead._id,
          groupByDate: format(lead.dateISO ?? "", DATE_PATTERN_SIMPLE),
          filterGroup: TimelineFilter.INQUIRIES,
          tag: TimelineItemTag.INQUIRY,
          leadType: lead.type ?? undefined,
          title: getMultilingualString(lead.name ?? {}) ?? "",
          description: getMultilingualString(lead.description ?? {}) ?? "",
          status: lead.status,
        })) ?? []),
      // Inquiry widget submissions (activity log, opens SurveyResponseViewer)
      ...(activityLogsData?.activityLogMany ?? [])
        .filter(log => log.type === EnumActivityLogType.InquiryReceived)
        .map(log => {
          const groupByDate = format(log.createdDateISO, DATE_PATTERN_SIMPLE);
          const msg = log.message as
            | { en?: string | null; fr?: string | null; es?: string | null }
            | null
            | undefined;
          const name = log.entityName as
            | { en?: string | null; fr?: string | null; es?: string | null }
            | null
            | undefined;
          const messageStr =
            (msg?.en?.trim() || msg?.fr?.trim() || msg?.es?.trim() || "") as string;
          const entityNameStr =
            (name?.en?.trim() || name?.fr?.trim() || name?.es?.trim() || "") as string;
          const initiatedByStr =
            typeof log.initiatedBy === "string" ? log.initiatedBy.trim() : "";
          return {
            _id: log._id,
            groupByDate,
            filterGroup: TimelineFilter.INQUIRIES,
            tag: TimelineItemTag.INQUIRY,
            title: entityNameStr || initiatedByStr,
            description: messageStr || entityNameStr || "",
            surveyResponseId: log.surveyResponseId ?? undefined,
            initiatedBy: initiatedByStr || undefined,
          };
        }),
      // Change log items
      ...(changeLogData?.changeLogManyPersonDetails?.map(changeLog => ({
        _id: changeLog._id,
        groupByDate: format(
          changeLog.createdDateISO ?? "",
          DATE_PATTERN_SIMPLE,
        ),
        filterGroup: TimelineFilter.ACTIVITY,
        tag: TimelineItemTag.ACTIVITY,
        title: "",
        description: getMultilingualString(changeLog.message ?? {}) ?? "",
        operator: getMultilingualString(changeLog.changedEntity?.name ?? {}),
        organization: getMultilingualString(
          changeLog.changedEntityOwner?.name ?? {},
        ),
      })) ?? []),
      // Event submitted items (these will fall into the activity filter group)
      ...(activityLogsData?.activityLogMany ?? [])
        .filter(log => log.type === EnumActivityLogType.EventSubmitted)
        .map(log => ({
          _id: log._id,
          groupByDate: format(log.createdDateISO, DATE_PATTERN_SIMPLE),
          filterGroup: TimelineFilter.ACTIVITY,
          tag: TimelineItemTag.ACTIVITY,
          title: getMultilingualString(log.entityName ?? {}) ?? "",
          description:
            getMultilingualString(log.message ?? {}) ||
            getMultilingualString(log.entityName ?? {}) ||
            "",
          eventId: log.entity._entityId,
        })),
      // Note items (exclude residual membership-kind notes)
      ...(notes
        ?.filter(note => !MEMBERSHIP_NOTE_KINDS.has(note.kind))
        .map(note => {
          const groupByDate = note.noteDateISO
            ? format(toZonedTime(note.noteDateISO, "UTC"), DATE_PATTERN_SIMPLE)
            : format(note.createdDateISO ?? "", DATE_PATTERN_SIMPLE);
          return {
            _id: note._id,
            groupByDate,
            filterGroup: TimelineFilter.NOTES,
            tag: TimelineItemTag.NOTE,
            title: "",
            description: "",
            note,
          };
        }) ?? []),
      // Membership events
      ...(membershipData?.membershipTimelineMany ?? []).map(item => ({
        _id: item._id,
        groupByDate: format(
          toZonedTime(item.createdDateISO, "UTC"),
          DATE_PATTERN_SIMPLE,
        ),
        filterGroup: TimelineFilter.MEMBERSHIP,
        tag: TimelineItemTag.MEMBERSHIP,
        title: item.tagName,
        description: "",
        membershipKind: item.kind,
        membershipViaSuffix: item.viaSuffix,
        membershipActorName: item.actorName,
      })),
      // Activity log items (for broadcasts ONLY ... Filter out event submitted + inquiry widget logs)
      ...(activityLogsData?.activityLogMany ?? [])
        .filter(
          log =>
            log.type !== EnumActivityLogType.EventSubmitted &&
            log.type !== EnumActivityLogType.InquiryReceived,
        )
        .map(log => {
          const groupByDate = format(log.createdDateISO, DATE_PATTERN_SIMPLE);
          // Read message/entityName directly; API returns { en?, fr?, es? }
          const msg = log.message as
            | { en?: string | null; fr?: string | null; es?: string | null }
            | null
            | undefined;
          const name = log.entityName as
            | { en?: string | null; fr?: string | null; es?: string | null }
            | null
            | undefined;
          const messageStr =
            (msg?.en?.trim() || msg?.fr?.trim() || msg?.es?.trim() || "") as string;
          const entityNameStr =
            (name?.en?.trim() || name?.fr?.trim() || name?.es?.trim() || "") as string;
          const initiatedByStr = typeof log.initiatedBy === "string" ? log.initiatedBy.trim() : "";
          const fallbackDescription =
            log.surveyResponseId && initiatedByStr
              ? `${initiatedByStr} completed a form`
              : "";
          return {
            _id: log._id,
            broadcastId: log.entity._entityId,
            surveyResponseId: log.surveyResponseId ?? undefined,
            groupByDate,
            filterGroup: TimelineFilter.BROADCASTS,
            tag: TimelineItemTag.BROADCAST,
            title: entityNameStr || initiatedByStr,
            description:
              messageStr || entityNameStr || fallbackDescription,
            initiatedBy: initiatedByStr || undefined,
          };
        }),
    ];

    // Now we need to group the items by date
    const timelineGroupsMap = new Map<string, ITimelineGroup>();
    allItems.forEach(item => {
      const groupByDate = item.groupByDate;
      if (!timelineGroupsMap.has(groupByDate)) {
        timelineGroupsMap.set(groupByDate, { groupByDate, items: [] });
      }
      timelineGroupsMap.get(groupByDate)?.items.push(item);
    });
    const timelineGroups = _.orderBy(
      Array.from(timelineGroupsMap.values()),
      "groupByDate",
      "desc",
    );

    setTimelineGroups(timelineGroups);
    setFilteredTimelineGroups(timelineGroups);
  }, [
    person,
    changeLogData?.changeLogManyPersonDetails,
    notes,
    activityLogsData?.activityLogMany,
    membershipData?.membershipTimelineMany,
  ]);

  // The filter options and selected filter state
  const filterOptions = useMemo(
    () =>
      Object.values(TimelineFilter).map(filter => ({
        label: t(`ONLY_SHOW.${filter}`),
        value: filter,
      })),
    [t],
  );
  const [selectedFilter, setSelectedFilter] = useState<TimelineFilter | null>(
    null,
  );

  /**
   * Handles the click event for a filter option.
   * - When the filters are changed, we need to filter the timeline items
   * - Users can only select one filter at a time
   * @param filter - The filter option to be clicked.
   */
  const handleFilterClick = (filter: TimelineFilter) => {
    const newSelectedFilter = selectedFilter === filter ? null : filter;
    setSelectedFilter(newSelectedFilter);
    setFilteredTimelineGroups(
      !newSelectedFilter
        ? timelineGroups
        : timelineGroups
            .map(group => {
              return {
                ...group,
                items: group.items.filter(
                  item => item.filterGroup === newSelectedFilter,
                ),
              };
            })
            .filter(group => group.items.length > 0),
    );
  };

  /**
   * Handle adding a new note
   */
  const [isAddingNote, setIsAddingNote] = useState(false);
  const handleNoteSaveSuccess = () => {
    setIsAddingNote(false);
  };

  return {
    filterOptions,
    selectedFilter,
    timelineGroups,
    filteredTimelineGroups,
    loading: changeLogLoading || notesLoading || activityLogsLoading || membershipLoading,
    changeLogError,
    notesError,
    activityLogsError,
    membershipError,
    handleFilterClick,
    isAddingNote,
    setIsAddingNote,
    handleNoteSaveSuccess,
  };
};
